/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Activity, Loader2, RefreshCw, Link2, Link2Off, AlertCircle,
  CheckCircle2, Clock, Zap,
} from 'lucide-react';

/**
 * WhoopConnectCard — reusable, subscriber-agnostic.
 * Renders three states: not_connected | connected | error/token_expired.
 * Also renders a gentle informational card when WHOOP isn't configured yet
 * on the platform or the admin hasn't enabled wearable access for this user.
 *
 * Strict rules:
 *   - Never displays raw tokens (backend never sends them).
 *   - Never displays raw WHOOP payload.
 *   - Does not replace manual entry or screenshot upload — mounts above them.
 */
export default function WhoopConnectCard({ subscriberId, onChanged }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    if (!subscriberId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/wearables/integration/${subscriberId}?provider=whoop`);
      setState(r.data);
    } catch (e) {
      setState(null);
    } finally {
      setLoading(false);
    }
  }, [subscriberId]);

  useEffect(() => { reload(); }, [reload]);

  // On page return from WHOOP OAuth, surface a toast + refresh state.
  useEffect(() => {
    try {
      const u = new URL(window.location.href);
      const flag = u.searchParams.get('whoop');
      const reason = u.searchParams.get('reason');
      if (flag === 'success') {
        toast.success('WHOOP connected.');
      } else if (flag === 'error') {
        toast.error(`WHOOP not connected (${reason || 'error'}).`);
      }
      if (flag) {
        u.searchParams.delete('whoop');
        u.searchParams.delete('reason');
        window.history.replaceState({}, '', u.toString());
        reload();
      }
    } catch { /* ignore */ }
  }, [reload]);

  const beginConnect = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/wearables/connect/whoop?subscriber_id=${subscriberId}`);
      if (r.data?.authorization_url) {
        window.location.href = r.data.authorization_url;
      } else {
        toast.error('Could not start WHOOP connection.');
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      const code = typeof d === 'object' ? d?.code : null;
      if (code === 'whoop_not_configured') {
        toast.error('WHOOP is not configured on this platform yet.');
      } else if (code === 'wearable_access_not_enabled') {
        toast.info('Your care team has not enabled WHOOP for your account yet.');
      } else {
        toast.error('Could not start WHOOP connection.');
      }
    } finally {
      setBusy(false);
    }
  };

  const syncNow = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/wearables/sync/${subscriberId}/whoop?days=7`);
      toast.success(`Synced ${r.data?.days_synced ?? 0} day(s) from WHOOP.`);
      await reload();
      onChanged && onChanged();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === 'object' ? (d.message || d.code) : (d || 'Sync failed.');
      toast.error(`WHOOP sync: ${msg}`);
      await reload();
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect WHOOP? Historical signals will be preserved.')) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/wearables/disconnect/${subscriberId}/whoop`);
      toast.success('WHOOP disconnected.');
      await reload();
      onChanged && onChanged();
    } catch {
      toast.error('Could not disconnect WHOOP.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div
        data-testid="whoop-connect-loading"
        className="rounded-xl border border-[#EBE8E3] p-4 flex items-center gap-3 text-sm text-[#8A8681]"
      >
        <Loader2 className="w-4 h-4 animate-spin" /> Loading WHOOP status…
      </div>
    );
  }

  // Not configured / not enabled / not connected
  if (!state || state.status === 'not_connected' || state.status === 'revoked') {
    const configured = state?.whoop_configured ?? false;
    const enabled = state?.wearable_access_enabled ?? false;

    return (
      <div
        data-testid="whoop-connect-card"
        data-whoop-state="not_connected"
        className="rounded-xl border border-[#EBE8E3] bg-white p-4 sm:p-5"
      >
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-[#F4F2EE] flex items-center justify-center flex-shrink-0">
            <Activity className="w-4 h-4 text-[#4A5D4E]" strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline justify-between gap-3 mb-1">
              <p className="text-sm font-['Work_Sans'] font-medium text-[#2C2A29]">
                Connect your WHOOP strap
              </p>
              <span
                data-testid="whoop-provider-badge"
                className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-[#F4F2EE] text-[#8A8681]"
              >
                WHOOP · Official
              </span>
            </div>
            <p className="text-xs text-[#8A8681] font-['Work_Sans'] leading-relaxed">
              Sync sleep, recovery, HRV and strain directly — no screenshots needed.
              You can always enter data manually below.
            </p>

            {!configured ? (
              <div
                data-testid="whoop-not-configured-note"
                className="mt-3 rounded-lg border border-[#EBE8E3] bg-[#F4F2EE] px-3 py-2 text-[11px] font-['Work_Sans'] text-[#8A8681] flex items-center gap-2"
              >
                <Clock className="w-3 h-3" strokeWidth={1.5} />
                WHOOP sign-in will be enabled here soon. Meanwhile, use manual entry or screenshot upload below.
              </div>
            ) : !enabled ? (
              <div
                data-testid="whoop-access-locked-note"
                className="mt-3 rounded-lg border border-[#EBE8E3] bg-[#F4F2EE] px-3 py-2 text-[11px] font-['Work_Sans'] text-[#8A8681] flex items-center gap-2"
              >
                <Clock className="w-3 h-3" strokeWidth={1.5} />
                Your care team will enable WHOOP sign-in for your account.
              </div>
            ) : (
              <Button
                data-testid="whoop-connect-btn"
                onClick={beginConnect}
                disabled={busy}
                className="mt-3 bg-[#4A5D4E] hover:bg-[#3D4F40] text-white font-['Work_Sans'] text-xs rounded-full px-5"
              >
                {busy ? <><Loader2 className="w-3 h-3 animate-spin" /> Starting…</>
                       : <><Link2 className="w-3 h-3" /> Connect WHOOP</>}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Error / token expired
  if (state.status === 'token_expired' || state.status === 'error') {
    return (
      <div
        data-testid="whoop-connect-card"
        data-whoop-state={state.status}
        className="rounded-xl border-2 border-amber-200 bg-amber-50/40 p-4 sm:p-5"
      >
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-700 flex-shrink-0" strokeWidth={1.5} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-['Work_Sans'] font-medium text-amber-900">
              WHOOP connection needs attention
            </p>
            <p className="text-xs text-amber-900/80 font-['Work_Sans'] mt-1">
              We could not reach your WHOOP account. You can reconnect below.
              Manual entry and screenshot upload remain available.
            </p>
            <div className="flex gap-2 mt-3">
              <Button
                data-testid="whoop-reconnect-btn"
                onClick={beginConnect}
                disabled={busy || !state.whoop_configured}
                className="bg-amber-600 hover:bg-amber-700 text-white font-['Work_Sans'] text-xs rounded-full px-4"
              >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Link2 className="w-3 h-3" />} Reconnect
              </Button>
              <Button
                data-testid="whoop-disconnect-btn"
                onClick={disconnect}
                variant="outline"
                disabled={busy}
                className="border-[#D8D4CE] text-[#5C5955] text-xs rounded-full px-4"
              >
                <Link2Off className="w-3 h-3" /> Disconnect
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Connected
  return (
    <div
      data-testid="whoop-connect-card"
      data-whoop-state="connected"
      className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4 sm:p-5"
    >
      <div className="flex items-start gap-3">
        <CheckCircle2 className="w-5 h-5 text-emerald-700 flex-shrink-0" strokeWidth={1.5} />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-3">
            <p className="text-sm font-['Work_Sans'] font-medium text-emerald-900">
              Connected to WHOOP
            </p>
            <span className="text-[10px] uppercase tracking-[0.18em] text-emerald-700/80 font-['Work_Sans']">
              Live
            </span>
          </div>
          <p
            data-testid="whoop-last-sync"
            className="text-xs text-emerald-900/70 font-['Work_Sans'] mt-1"
          >
            {state.last_successful_sync_at
              ? `Last synced ${new Date(state.last_successful_sync_at).toLocaleString()}`
              : 'Not synced yet — tap Sync Now to pull your data.'}
          </p>
          <div className="flex gap-2 mt-3">
            <Button
              data-testid="whoop-sync-now-btn"
              onClick={syncNow}
              disabled={busy}
              className="bg-emerald-700 hover:bg-emerald-800 text-white font-['Work_Sans'] text-xs rounded-full px-4"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Sync Now
            </Button>
            <Button
              data-testid="whoop-disconnect-btn"
              onClick={disconnect}
              variant="outline"
              disabled={busy}
              className="border-[#D8D4CE] text-[#5C5955] text-xs rounded-full px-4"
            >
              <Link2Off className="w-3 h-3" /> Disconnect
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
